import { useAmplitude } from '@/context/me/amplitude.context';
import { useAnalytics } from '@cmpsr/analytics';
import { MULTI_VERTICAL_EXPERIMENT_NAME } from '@/constants/experiments';
import { getPurchaseModel } from '@/hooks/use-purchase-model';

// Dashboard source tracking for Page Viewed and Modal Viewed events
export enum DashboardSource {
  DASHBOARD_SPECIFIC_CANCER = 'dashboard_specific_cancer',
  DASHBOARD_CANCER_CTA = 'dashboard_cancer_CTA',
  DASHBOARD_SPECIFIC_RISK_FACTOR = 'dashboard_specific_risk_factor',
  DASHBOARD_RISK_FACTOR_CTA = 'dashboard_risk_factor_CTA',
  DASHBOARD_SPECIFIC_TEST = 'dashboard_specific_test',
  DASHBOARD_TEST_CTA = 'dashboard_test_CTA',
  DASHBOARD_SPECIFIC_ACTION = 'dashboard_specific_action',
  DASHBOARD_ACTION_CTA = 'dashboard_action_CTA',
  DASHBOARD_HEALTH_DATA_CTA = 'dashboard_health_data_CTA',
  DASHBOARD_LABS_CTA = 'dashboard_labs_CTA',
  DASHBOARD_LABS_HERO = 'dashboard_labs_hero',
  RISK_SCORE_REVEAL = 'risk_score_reveal',
  BLOODWORK_REVEAL = 'bloodwork_reveal',
}

// MODULE-LEVEL source tracking (shared across all hook instances)
let currentNavigationSource: DashboardSource | null = null;

// MODULE-LEVEL additional params tracking (shared across all hook instances)
let sharedAdditionalParams: Record<string, unknown> | null = null;

// This hook centralizes analytics calls shaping and defining them according
// to their definition by the product team found here: https://docs.google.com/spreadsheets/d/1YMh5n5VeEizcfkqgoH3qgPrxcRnSTFvHd96yCD23Dj8/
export default function useAnalyticsCalls() {
  const analytics = useAnalytics();
  const amplitude = useAmplitude();

  const analyticsWrapper = {
    track: (eventName: string, traits: Record<string, string | number> = {}) => {
      analytics.track(eventName, traits);
      amplitude.track(eventName, traits);
    },
    identify: (userId: string, traits: Record<string, string | number> = {}) => {
      amplitude.setUserId(userId);
      if (traits) {
        const identifyEvent = new amplitude.Identify();
        Object.entries(traits).forEach(([key, value]) => {
          identifyEvent.set(key, value);
        });
        amplitude.identify(identifyEvent);
      }
    },
  };

  const setNavigationSource = (source: DashboardSource) => {
    currentNavigationSource = source;
  };

  const setAnalyticsParams = (additionalParams: Record<string, unknown>) => {
    sharedAdditionalParams = { ...additionalParams };
  };

  const callPageView = (additionalParams: Record<string, unknown> = {}) => {
    // Consume source (used once then cleared)
    const source = currentNavigationSource;
    currentNavigationSource = null;

    // Combine module-level additional params with provided params, then clear
    const combinedParams = {
      ...sharedAdditionalParams,
      ...additionalParams,
    };
    sharedAdditionalParams = null;


    analyticsWrapper.track('[Amplitude] Page Viewed', {
      '[Amplitude] Page Domain': window.location.hostname,
      '[Amplitude] Page Location': window.location.href,
      '[Amplitude] Page Path': window.location.pathname,
      '[Amplitude] Page Title': window.document.title,
      '[Amplitude] Page URL': window.location.href,
      // Use provided referrer from additionalParams (for SPA navigation) or fall back to document.referrer (for external navigation)
      referrer: (additionalParams.referrer as string) || document.referrer,
      page_name: window.location.pathname,
      ...(source && { source }), // Only include source if it exists
      ...combinedParams,
    });
  };

  const callIdentifyEmail = (id: string, email: string) => analyticsWrapper.identify(id, { email });

  const callIdentifyMembership = (id: string, email: string, userType: string) => {
    const purchaseModel = getPurchaseModel();
    const traits: Record<string, string | number> = {
      email,
      user_type: userType,
      membership_start_date: new Date().toISOString(),
      purchase_model: purchaseModel,
    };
    if (purchaseModel === 'direct') {
      traits.is_trial_user = 'false';
    } else {
      traits.is_trial_user = 'true';
      traits.is_trial_conversion_eligible = 'false';
    }
    analyticsWrapper.identify(id, traits);
  };

  const callIdentifySignUp = (id: string, email: string, userType: string) => {
    const purchaseModel = getPurchaseModel();
    const traits: Record<string, string | number> = {
      email,
      user_type: userType,
      account_creation_date: new Date().toISOString(),
      purchase_model: purchaseModel,
    };
    if (purchaseModel === 'direct') {
      traits.is_trial_user = 'false';
    } else {
      traits.is_trial_user = 'true';
      traits.is_trial_conversion_eligible = 'false';
    }
    analyticsWrapper.identify(id, traits);
  };

  // Set multi-vertical experiment user property (matches marketing site property)
  const setMultiVerticalExperimentProperty = () => {
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set(MULTI_VERTICAL_EXPERIMENT_NAME, true);
    amplitude.identify(identifyEvent);
  };

  // Set positioning variant user property (payment page A/B framing).
  const setPositioningVariantProperty = (variant: string) => {
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set('positioning_variant', variant);
    amplitude.identify(identifyEvent);
  };

  // Join Funnel - per-step page view. Fires on mount of each of the 3 steps.
  const callFunnelStepViewed = (
    step: 1 | 2 | 3,
    positioningVariant: string,
  ) =>
    analyticsWrapper.track('Funnel - Step Viewed', {
      step,
      positioning_variant: positioningVariant,
      page_path: window.location.pathname,
    });

  // Join Funnel - per-field capture. Fires on blur after a successful PATCH.
  // PII (email/name/dob) is sent raw per spec — Amplitude already receives it
  // via identify on existing flows. For DOB we additionally send the derived
  // age integer so dashboards can bucket without re-deriving.
  const callFunnelFieldCaptured = (params: {
    field: 'first_name' | 'last_name' | 'email' | 'date_of_birth' | 'biological_sex' | 'state';
    value: string;
    age?: number;
    positioningVariant: string;
  }) => {
    const eventProps: Record<string, string | number> = {
      field: params.field,
      value: params.value,
      positioning_variant: params.positioningVariant,
    };
    if (params.age !== undefined) {
      eventProps['age'] = params.age;
    }
    analyticsWrapper.track('Funnel - Field Captured', eventProps);
  };

  // Join Funnel - age error shown. Fires when the under-18 or over-85 inline
  // warning becomes visible (not on every keystroke).
  const callFunnelAgeErrorShown = (params: {
    errorType: 'under_18' | 'over_85';
    age: number;
    positioningVariant: string;
  }) =>
    analyticsWrapper.track('Funnel - Age Error Shown', {
      error_type: params.errorType,
      age: params.age,
      positioning_variant: params.positioningVariant,
    });

  // Join Funnel - lab unavailable shown. Fires when the user selects RI or HI
  // on Step 3 and the warning card renders.
  const callFunnelLabUnavailableShown = (state: string, positioningVariant: string) =>
    analyticsWrapper.track('Funnel - Lab Unavailable Shown', {
      state,
      positioning_variant: positioningVariant,
    });

  const callSignInStarted = () =>
    analyticsWrapper.track('Sign In - Started', {
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: window.document.title,
    });

  const callSignInCompleted = () =>
    analyticsWrapper.track('Sign In - Completed', {
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: window.document.title,
    });

  const callLogoutCompleted = (type = 'manual') =>
    analyticsWrapper.track('Log Out - Completed', {
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: window.document.title,
      logout_method: type,
    });

  const callOnboardingStarted = () => analyticsWrapper.track('Onboarding - Started');

  const callOnboardingCompleted = () => analyticsWrapper.track('Onboarding - Completed');

  const callOnboardingSectionCompleted = (onboardingSection: string) =>
    analyticsWrapper.track('Onboarding - Section Completed', {
      onboarding_section: onboardingSection,
    });

  const callOnboardingViewScreen = (screenName: string, section: string, questionId: string) =>
    analyticsWrapper.track('Onboarding Screen - Viewed', {
      screen_name: screenName,
      onboarding_section: section,
      question_number: questionId,
    });

  const callPaymentScreenViewed = (
    baseAmount: number,
    discountAmount: number,
    finalAmount: number,
    positioningVariant?: string,
  ) =>
    analyticsWrapper.track('Payment - Screen Viewed', {
      list_price_displayed: baseAmount,
      discount_displayed: discountAmount,
      total_price_displayed: finalAmount,
      ...(positioningVariant ? { positioning_variant: positioningVariant } : {}),
    });

  const callPaymentStripeLoadFailed = () =>
    analyticsWrapper.track('Payment - Stripe Load Failed', {
      page_url: window.location.href,
      page_path: window.location.pathname,
    });

  const callRevenue = (revenue: number, type = 'new') => analytics.revenue(revenue, 1, type);

  const callMembershipCancel = () => analyticsWrapper.track('Membership - Initiate Cancel');

  const callMembershipRenew = () => analyticsWrapper.track('Membership - Initiate Renewal');

  const callActionPlanItemCompleted = (itemId: string, itemDescription: string) =>
    analyticsWrapper.track('Action Plan - Mark Item Completed', {
      action_plan_item_id: itemId,
      action_plan_item_description: itemDescription,
    });

  const callRiskFactorViewed = (riskFactor: string, riskFactorSlug: string) =>
    callModalViewed('Risk Factor', {
      risk_factor: riskFactor,
      risk_factor_slug: riskFactorSlug,
    });

  const callPpraModalViewed = (surveyItemName: string) =>
    callModalViewed('PPRA', {
      survey_item_name: surveyItemName,
    });

  const callPpraFakePaymentSubmitted = (amount: string) =>
    analyticsWrapper.track('Sign Up - Payment Submitted', {
      form_id: 'ppra-payment-form',
      form_name: 'PPRA Payment Form',
      account_type: 'user',
      payment_method: 'unknown',
      payment_status: 'incomplete',
      promo_code: `PPRA${amount}`,
      coupon_id: `PPRA${amount}`,
      subscription_id: `PPRA${amount}`,
      payment_amount: amount,
    });

  const callModalViewed = (group: string, additionalParams: Record<string, string> = {}) => {
    // Consume source (used once then cleared)
    const source = currentNavigationSource;
    currentNavigationSource = null;

    analyticsWrapper.track(`Modal Viewed`, {
      modal_group: group,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      ...(source && { source }), // Only include source if it exists
      ...additionalParams,
    });
  };

  const callTestResultsModalViewed = (testTitle: string, testSlug: string) =>
    callModalViewed('Test Results', {
      test_title: testTitle,
      test_slug: testSlug,
    });

  const callActionPlanInterventionViewed = (interventionTitle: string, interventionSlug: string) =>
    callModalViewed('Action Plan Intervention', {
      intervention_title: interventionTitle,
      intervention_slug: interventionSlug,
    });

  const callAnswerSubmitted = (
    sectionName: string,
    screenName: string,
    answerValue: string | number,
    questionnaireType: string,
    questionId: string,
    questionType: string,
  ) => {
    analyticsWrapper.track('Answer Submitted', {
      section_name: sectionName,
      screen_name: screenName,
      answer_value: answerValue,
      questionnaire_type: questionnaireType,
      question_id: questionId,
      question_type: questionType,
    });
  };

  const callLinkClicked = (linkText: string, linkUrl: string) => {
    const linkPath = linkUrl ? new URL(linkUrl).pathname : '';
    analyticsWrapper.track('Link Clicked', {
      link_text: linkText,
      link_url: linkUrl,
      link_path: linkPath,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callToggleRisk = (previousRiskType: string, newRiskType: string) => {
    analyticsWrapper.track('Toggle Risk', {
      previous_risk_type: previousRiskType,
      new_risk_type: newRiskType,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callDashboardTabChanged = (previousTab: string, newTab: string) => {
    analyticsWrapper.track('Dashboard Tab Changed', {
      previous_tab: previousTab,
      new_tab: newTab,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callHealthDataCategoryToggled = (categoryName: string, isEditing: boolean) => {
    analyticsWrapper.track('Health Data Toggle', {
      category_toggled: categoryName.toLowerCase(),
      is_editing: isEditing ? 'yes' : 'no',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callDownloadLabs = (type: string) => {
    analyticsWrapper.track('Download Labs', {
      type: type,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callReceiptDownloaded = (subscriptionId: string) => {
    analyticsWrapper.track('Receipt Downloaded', {
      subscription_id: subscriptionId,
      source: 'account_page',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callProductPaywall = (paywallType: 'Subscribe' | 'Fix Payment' | 'Fix Payment + Cancel') => {
    analyticsWrapper.track('Product Paywall', {
      paywall_type: paywallType,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callProductPaymentPage = (source: 'paywall' | 'account') => {
    analyticsWrapper.track('Product Payment Page', {
      source: source,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callWinbackAccepted = (price: number, discountAmount: number, source?: string) => {
    analyticsWrapper.track('Winback Accepted', {
      price: price,
      discount_amount: discountAmount,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      ...(source && { source }),
    });
  };

  const callLetterToDoctorCTAClicked = () => {
    analyticsWrapper.track('Clicked Letter to Doctor CTA', {
      source: 'dashboard',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callDownloadPDFClicked = () => {
    analyticsWrapper.track('Clicked Download PDF', {
      source: 'dashboard',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callDownloadLetterClicked = () => {
    analyticsWrapper.track('Clicked Download Letter', {
      source: 'dashboard',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callLetterGenerationStarted = () => {
    analyticsWrapper.track('Letter Generation Started', {
      source: 'dashboard',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callLetterGenerationRetry = () => {
    analyticsWrapper.track('Letter Generation Retry', {
      source: 'dashboard',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callWinbackDeclined = (source?: string) => {
    analyticsWrapper.track('Winback Declined', {
      ...(source && { source }),
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callWearableModalOpened = (source: string) =>
    analyticsWrapper.track('Wearable - Modal Opened', { source });

  const callWearableConnectStarted = (providerSlug: string) =>
    analyticsWrapper.track('Wearable - Connect Started', { provider_slug: providerSlug });

  const callWearableConnectionCompleted = (providerSlug: string, source: string) =>
    analyticsWrapper.track('Wearable - Connection Completed', {
      provider_slug: providerSlug,
      source,
    });

  const callWearableModalAbandoned = (providerSlug: string | null, lastStep: string) =>
    analyticsWrapper.track('Wearable - Modal Abandoned', {
      ...(providerSlug && { provider_slug: providerSlug }),
      last_step: lastStep,
    });

  const callWearableDisconnected = (providerSlug: string) =>
    analyticsWrapper.track('Wearable - Disconnected', { provider_slug: providerSlug });

  const callWearableSyncClicked = (providerSlug: string, source: string) =>
    analyticsWrapper.track('Wearable - Sync Clicked', {
      provider_slug: providerSlug,
      source,
    });

  const callMultiDiseaseModalViewed = (step: string, cohort?: string) => {
    callModalViewed('Multi-Disease Experiment', {
      step,
      ...(cohort && { cohort }),
    });
  };

  const callTrialPrequelPageViewed = (slug: string, experimentVariant: string) => {
    const pageTitle = `Trial Prequel ${slug}`;
    analyticsWrapper.track('[Amplitude] Page Viewed', {
      '[Amplitude] Page Domain': window.location.hostname,
      '[Amplitude] Page Location': window.location.href,
      '[Amplitude] Page Path': window.location.pathname,
      '[Amplitude] Page Title': pageTitle,
      '[Amplitude] Page URL': window.location.href,
      referrer: document.referrer,
      page_name: window.location.pathname,
      page_title: pageTitle,
      experiment_variant: experimentVariant,
    });
  };

  const callTrialPrequelAnswerSubmitted = (screenName: string, answerValue: string, experimentVariant: string) => {
    analyticsWrapper.track('Trial Prequel Answer Submitted', {
      screen_name: screenName,
      answer_value: answerValue,
      experiment_variant: experimentVariant,
    });
  };

  const callLabReportUploadStarted = (fileCount: number, totalSizeMB: number) => {
    analyticsWrapper.track('Lab Report Upload Started', {
      file_count: fileCount,
      total_size_mb: totalSizeMB,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callLabReportUploadCompleted = (
    uploadId: string,
    isDuplicate: boolean,
    processingTimeMs: number,
  ) => {
    analyticsWrapper.track('Lab Report Upload Completed', {
      upload_id: uploadId,
      is_duplicate: isDuplicate ? 'yes' : 'no',
      processing_time_ms: processingTimeMs,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callLabReportUploadFailed = (error: string, fileSize: number) => {
    analyticsWrapper.track('Lab Report Upload Failed', {
      error_message: error,
      file_size_bytes: fileSize,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callLabReportParsingCompleted = (
    uploadId: string,
    biomarkerCount: number,
    overallConfidence: number,
    needsReview: boolean,
  ) => {
    analyticsWrapper.track('Lab Report Parsing Completed', {
      upload_id: uploadId,
      biomarker_count: biomarkerCount,
      overall_confidence: Math.round(overallConfidence * 100),
      needs_review: needsReview ? 'yes' : 'no',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callLabReportReviewStarted = (uploadId: string, biomarkerCount: number) => {
    analyticsWrapper.track('Lab Report Review Started', {
      upload_id: uploadId,
      biomarker_count: biomarkerCount,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callLabReportReviewCompleted = (
    uploadId: string,
    accepted: boolean,
    editedCount: number,
    reviewTimeMs: number,
    matchedCount: number,
    totalCount: number,
    dismissedCount = 0,
  ) => {
    analyticsWrapper.track('Lab Report Review Completed', {
      upload_id: uploadId,
      review_action: accepted ? 'accepted' : 'rejected',
      edited_biomarker_count: editedCount,
      review_time_ms: reviewTimeMs,
      matched_count: matchedCount,
      total_count: totalCount,
      dismissed_count: dismissedCount,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    });
  };

  const callLabReportModalViewed = (modalType: 'upload' | 'review' | 'list') => {
    callModalViewed('Lab Report Upload', {
      modal_type: modalType,
    });
  };

  const callBloodworkRevealStarted = (traits: {
    comparison?: string | null;
    marker_state?: string | null;
    frame_count: number;
    bio_age_delta?: number | null;
  }) => {
    analyticsWrapper.track('Bloodwork Reveal - Started', {
      comparison: traits.comparison ?? 'unknown',
      marker_state: traits.marker_state ?? 'unknown',
      frame_count: traits.frame_count,
      ...(traits.bio_age_delta !== undefined && traits.bio_age_delta !== null && { bio_age_delta: traits.bio_age_delta }),
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: window.document.title,
    });
  };

  const callBloodworkRevealFrameViewed = (traits: {
    frame_index: number;
    frame_count: number;
    graphic_name: string | null;
  }) => {
    analyticsWrapper.track('Bloodwork Reveal - Frame Viewed', {
      frame_index: traits.frame_index,
      frame_count: traits.frame_count,
      graphic_name: traits.graphic_name ?? 'text-only',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: window.document.title,
    });
  };

  const callBloodworkRevealCompleted = (traits: { frame_count: number }) => {
    analyticsWrapper.track('Bloodwork Reveal - Completed', {
      frame_count: traits.frame_count,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: window.document.title,
    });
  };

  const callBloodworkRevealDismissedEarly = (traits: {
    reason: 'error' | 'empty-frames' | 'unload' | 'no-router-state';
    last_frame_index?: number;
  }) => {
    analyticsWrapper.track('Bloodwork Reveal - Dismissed Early', {
      reason: traits.reason,
      ...(traits.last_frame_index !== undefined && { last_frame_index: traits.last_frame_index }),
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: window.document.title,
    });
  };

  const callMultiDiseaseFeedbackSubmitted = (
    healthPriorities: {
      cancer: boolean;
      heartDisease: boolean;
      dementia: boolean;
      diabetes: boolean;
    },
    flowPath: 'continue' | 'cancel',
    hasAdditionalFeedback: boolean,
  ) => {
    // Convert priorities to comma-separated string for analytics
    const priorities: string[] = [];
    if (healthPriorities.cancer) priorities.push('cancer');
    if (healthPriorities.heartDisease) priorities.push('heart_disease');
    if (healthPriorities.dementia) priorities.push('dementia');
    if (healthPriorities.diabetes) priorities.push('diabetes');

    analyticsWrapper.track('Multi-Disease Feedback Submitted', {
      health_priorities: priorities.join(','),
      flow_path: flowPath,
      has_additional_feedback: hasAdditionalFeedback ? 'yes' : 'no',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      source: MULTI_VERTICAL_EXPERIMENT_NAME,
    });
  };

  return {
    callIdentifyEmail,
    callIdentifyMembership,
    callIdentifySignUp,
    callSignInStarted,
    callSignInCompleted,
    callLogoutCompleted,
    callOnboardingStarted,
    callOnboardingCompleted,
    callOnboardingSectionCompleted,
    callOnboardingViewScreen,
    callRevenue,
    callMembershipCancel,
    callMembershipRenew,
    callActionPlanItemCompleted,
    callPaymentScreenViewed,
    callPaymentStripeLoadFailed,
    callRiskFactorViewed,
    callPpraModalViewed,
    callPpraFakePaymentSubmitted,
    callTestResultsModalViewed,
    callActionPlanInterventionViewed,
    callModalViewed,
    callPageView,
    callAnswerSubmitted,
    callLinkClicked,
    callToggleRisk,
    callDashboardTabChanged,
    callHealthDataCategoryToggled,
    callDownloadLabs,
    callReceiptDownloaded,
    callProductPaywall,
    callProductPaymentPage,
    callWinbackAccepted,
    callLetterToDoctorCTAClicked,
    callDownloadPDFClicked,
    callDownloadLetterClicked,
    callLetterGenerationStarted,
    callLetterGenerationRetry,
    callWinbackDeclined,
    callMultiDiseaseModalViewed,
    callMultiDiseaseFeedbackSubmitted,
    setMultiVerticalExperimentProperty,
    setPositioningVariantProperty,
    callFunnelStepViewed,
    callFunnelFieldCaptured,
    callFunnelAgeErrorShown,
    callFunnelLabUnavailableShown,
    setNavigationSource,
    setAnalyticsParams,
    callTrialPrequelPageViewed,
    callTrialPrequelAnswerSubmitted,
    callLabReportUploadStarted,
    callLabReportUploadCompleted,
    callLabReportUploadFailed,
    callLabReportParsingCompleted,
    callLabReportReviewStarted,
    callLabReportReviewCompleted,
    callLabReportModalViewed,
    callWearableModalOpened,
    callWearableConnectStarted,
    callWearableConnectionCompleted,
    callWearableModalAbandoned,
    callWearableDisconnected,
    callWearableSyncClicked,
    callBloodworkRevealStarted,
    callBloodworkRevealFrameViewed,
    callBloodworkRevealCompleted,
    callBloodworkRevealDismissedEarly,
  };
}
